<template>
  <div class="table-wrap">
    <my-loader v-if="isLoading && !cars.length" />

    <div v-else class="table-scroll">
      <table
        v-if="cars.length"
        class="cars-table"
        :style="{ width: `${table.getTotalSize()}px` }"
      >
        <thead>
          <tr v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
            <th
              v-for="header in headerGroup.headers"
              :key="header.id"
              :style="{ width: `${header.getSize()}px` }"
            >
              <span>{{ header.column.columnDef.header }}</span>
              <span
                class="column-resizer"
                :class="{ resizing: header.column.getIsResizing() }"
                title="Изменить ширину столбца"
                aria-hidden="true"
                @mousedown.stop="header.getResizeHandler()($event)"
                @touchstart.stop="header.getResizeHandler()($event)"
                @click.stop
                @dblclick.stop="header.column.resetSize()"
              />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in table.getRowModel().rows"
            :key="row.id"
            class="row-card"
            :class="{ 'row-placeholder': isPlaceholderData }"
            @click="onRowClick(row.original)"
          >
            <td
              v-for="cell in row.getVisibleCells()"
              :key="cell.id"
              :style="{ width: `${cell.column.getSize()}px` }"
            >
              <template v-if="cell.column.id === 'number'">
                <span :title="row.original.number">{{ row.original.number || '—' }}</span>
              </template>

              <template v-else-if="cell.column.id === 'avtocod_status'">
                <span
                  v-if="securityOf(row.original)"
                  :class="[
                    'status-badge',
                    securityOf(row.original).avtocod_is_end ? 'st-verified' : 'st-nverified',
                  ]"
                >
                  {{ securityOf(row.original).avtocod_is_end ? 'Проверено' : 'Не проверено' }}
                </span>
                <span v-else>—</span>
              </template>

              <template v-else-if="cell.column.id === 'black_list'">
                {{ securityOf(row.original)?.black_list ? '📛' : '—' }}
              </template>

              <template v-else-if="cell.column.id === 'black_list_client'">
                {{ securityOf(row.original)?.black_list_client ? '📛' : '—' }}
              </template>

              <template v-else-if="cell.column.id === 'ownerPhone'">
                <span :title="row.original.ownerPhone">{{ row.original.ownerPhone || '—' }}</span>
              </template>

              <template v-else-if="cell.column.id === 'vin'">
                <span :title="row.original.vin">{{ row.original.vin || '—' }}</span>
              </template>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="isRefreshing && cars.length" class="refresh-bar" />

      <div v-if="!cars.length && !isLoading" class="empty-table">
        Авто по выбранным фильтрам нет.
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { functionalUpdate, getCoreRowModel, useVueTable } from '@tanstack/vue-table'
import {
  CARS_PAGE_SIZE,
  carSecurityInfo,
  createCarsColumns,
} from '../carsState.mjs'

export default {
  name: 'CarsTable',
  props: {
    cars: { type: Array, default: () => [] },
    isLoading: { type: Boolean, default: false },
    isRefreshing: { type: Boolean, default: false },
    isPlaceholderData: { type: Boolean, default: false },
    columnOrder: { type: Array, default: () => [] },
    columnVisibility: { type: Object, default: () => ({}) },
    columnSizing: { type: Object, default: () => ({}) },
    pageCount: { type: Number, default: 1 },
    currentPage: { type: Number, default: 1 },
  },
  emits: ['columnSizingChanged'],
  setup(props, { emit }) {
    const router = useRouter()
    const cars = computed(() => (Array.isArray(props.cars) ? props.cars : []))
    const columns = createCarsColumns()

    const table = useVueTable({
      get data() {
        return cars.value
      },
      columns,
      getCoreRowModel: getCoreRowModel(),
      getRowId: (row, index) => String(row.id ?? `car-${index}`),
      manualPagination: true,
      manualSorting: true,
      get pageCount() {
        return props.pageCount
      },
      columnResizeMode: 'onChange',
      state: {
        get columnOrder() {
          return props.columnOrder
        },
        get columnVisibility() {
          return props.columnVisibility
        },
        get columnSizing() {
          return props.columnSizing
        },
        get pagination() {
          return {
            pageIndex: Math.max(0, props.currentPage - 1),
            pageSize: CARS_PAGE_SIZE,
          }
        },
      },
      onColumnSizingChange: (updater) => {
        emit('columnSizingChanged', functionalUpdate(updater, props.columnSizing))
      },
    })

    const onRowClick = (car) => {
      if (props.isPlaceholderData || !car?.id) return
      router.push(`/cars/${car.id}`)
    }

    return {
      table,
      cars,
      securityOf: carSecurityInfo,
      onRowClick,
    }
  },
}
</script>

<style scoped>
.table-wrap {
  width: 100%;
  position: relative;
}

.table-scroll {
  width: 100%;
  overflow-x: auto;
}

.cars-table {
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 13px;
  min-width: 100%;
}

.cars-table th,
.cars-table td {
  height: 32px;
  padding: 6px 8px;
  border-bottom: 1px solid #edf0f3;
  text-align: left;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  position: relative;
}

.cars-table th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f7f8fa;
  color: #444;
  font-weight: bold;
  user-select: none;
}

.cars-table td {
  font-weight: bold;
}

.row-card {
  cursor: pointer;
}

.row-card:hover {
  background-color: rgba(224, 187, 72, 0.4);
}

.row-placeholder {
  opacity: 0.72;
}

.column-resizer {
  position: absolute;
  top: 0;
  right: 0;
  width: 6px;
  height: 100%;
  cursor: col-resize;
  user-select: none;
  touch-action: none;
}

.column-resizer:hover,
.column-resizer.resizing {
  background: rgba(79, 70, 229, 0.35);
}

.empty-table {
  padding: 32px;
  color: #667085;
  font-size: 14px;
  text-align: center;
}

.refresh-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, #4f46e5, transparent);
  animation: refresh-slide 1s linear infinite;
}

@keyframes refresh-slide {
  from { transform: translateX(-100%); }
  to { transform: translateX(100%); }
}

.status-badge {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  border-radius: 10px;
  padding: 2px 7px;
  font-size: 12px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
}

.st-verified {
  background: #e6f4ea;
  color: #1e7e34;
}

.st-nverified {
  background: #fdeaea;
  color: #c0392b;
}
</style>
